import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BookOpen, Calendar, Download as DownloadIcon, FileText, GraduationCap, Megaphone, Plus, Search, Sparkles, UserPlus, Zap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardHeader, EmptyState, Avatar, Badge } from "@/components/ui-em";

function useGreeting() {
  const { t } = useTranslation();
  const h = new Date().getHours();
  if (h < 12) return t("dashboard.greetingMorning");
  if (h < 18) return t("dashboard.greetingAfternoon");
  return t("dashboard.greetingEvening");
}

function useTimeAgo() {
  const { t } = useTranslation();
  return (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return t("common.justNow");
    if (s < 3600) return t("common.minAgo", { n: Math.floor(s / 60) });
    if (s < 86400) return t("common.hourAgo", { n: Math.floor(s / 3600) });
    return t("common.dayAgo", { n: Math.floor(s / 86400) });
  };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [hero, setHero] = useState(null);
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const greetingText = useGreeting();
  const timeAgo = useTimeAgo();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/dashboard/hero-video").then((r) => setHero(r.data)).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/knowledge-base?q=${encodeURIComponent(search)}`);
  };

  const firstName = (user?.name || "").split(" ")[0] || "Leader";

  const quickActions = user?.role === "admin"
    ? [
        { label: t("dashboard.actions.newArticle"), to: "/knowledge-base/new", icon: Plus },
        { label: t("dashboard.actions.postAnnouncement"), to: "/admin/announcements", icon: Megaphone },
        { label: t("dashboard.actions.uploadFile"), to: "/admin/downloads", icon: DownloadIcon },
        { label: t("dashboard.actions.addUser"), to: "/admin/users", icon: UserPlus },
      ]
    : [
        { label: t("dashboard.actions.browseKnowledge"), to: "/knowledge-base", icon: BookOpen },
        { label: t("dashboard.actions.continueLearning"), to: "/academy", icon: GraduationCap },
        { label: t("dashboard.actions.viewDownloads"), to: "/downloads", icon: DownloadIcon },
        { label: t("dashboard.actions.latestAnnouncements"), to: "/announcements", icon: Megaphone },
      ];

  const localeMap = { nl: "nl-NL", en: "en-US", es: "es-ES" };
  const locale = localeMap[i18n.language?.split("-")[0]] || "nl-NL";

  const heroYoutubeId = React.useMemo(() => {
    if (!hero?.url) return null;
    try {
      const u = new URL(hero.url);
      if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
      return u.searchParams.get("v");
    } catch {
      return null;
    }
  }, [hero?.url]);

  return (
    <div data-testid="dashboard" className="space-y-10">
      {/* Header */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3 font-semibold">{t("dashboard.eyebrow")}</p>
        <h1 className="font-display text-5xl font-light tracking-tight text-white">
          {greetingText}, <span className="text-gold">{firstName}</span>.
        </h1>
        <p className="mt-4 text-zinc-500 text-sm max-w-2xl leading-relaxed">
          {t("dashboard.todayCopy")} · {new Date().toLocaleDateString(locale, { weekday: "long", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* Hero video */}
      {hero && heroYoutubeId && (
        <Card className="p-0 overflow-hidden" data-testid="dashboard-hero-video">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr]">
            <div className="relative bg-black">
              <div className="aspect-video">
                <iframe
                  data-testid="hero-video-iframe"
                  src={`https://www.youtube.com/embed/${heroYoutubeId}?rel=0&modestbranding=1`}
                  title={hero.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center gap-4 bg-gradient-to-br from-gold/[0.04] via-transparent to-transparent">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">
                  {t("dashboard.featuredInterview")}
                </p>
              </div>
              <h2 className="font-display text-2xl lg:text-3xl font-light tracking-tight text-white leading-tight">
                {hero.title}
              </h2>
              {hero.description && (
                <p className="text-sm text-zinc-400 leading-relaxed">{hero.description}</p>
              )}
              <div className="pt-2 flex items-center gap-3 text-xs text-zinc-500">
                <Sparkles className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
                <span>{t("dashboard.heroVideoAiHint")}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Search + Quick actions row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-8 p-8">
          <CardHeader
            eyebrow={t("nav.knowledgeBase")}
            title={t("dashboard.searchEverything")}
            action={
              <Link to="/knowledge-base" className="text-xs text-zinc-400 hover:text-gold flex items-center gap-1" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>
                {t("common.browseAll")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          />
          <form onSubmit={doSearch} className="relative">
            <Search className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
            <input
              data-testid="dashboard-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("dashboard.searchPlaceholder")}
              className="em-input pl-12 py-4 text-base"
            />
          </form>
        </Card>

        <Card className="lg:col-span-4 p-8">
          <CardHeader eyebrow={t("dashboard.quickActions")} title={t("dashboard.jumpRightIn")} />
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((a) => (
              <Link
                key={a.to + a.label}
                to={a.to}
                className="flex flex-col items-start gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/5 hover:border-gold/30 hover:bg-gold/[0.04] group"
                style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
              >
                <a.icon className="w-4 h-4 text-gold" strokeWidth={1.75} />
                <span className="text-xs text-zinc-300 group-hover:text-white leading-tight" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{a.label}</span>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Latest content bento */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Latest articles */}
        <Card className="lg:col-span-8 p-8">
          <CardHeader
            eyebrow={t("nav.knowledgeBase")}
            title={t("dashboard.newestArticles")}
            action={
              <Link to="/knowledge-base" className="text-xs text-zinc-400 hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>{t("common.all")}</Link>
            }
          />
          {data?.latest_articles?.length ? (
            <div className="divide-y divide-white/5">
              {data.latest_articles.map((a) => (
                <Link key={a.id} to={`/knowledge-base/${a.id}`} className="flex items-start gap-4 py-4 group">
                  <Avatar name={a.author} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white group-hover:text-gold truncate" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{a.title}</div>
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                      <span className="text-gold">{a.category}</span>
                      <span>·</span>
                      <span>{t("common.updated").toLowerCase()} {timeAgo(a.updated_at)}</span>
                      {a.reading_time > 0 && (<><span>·</span><span>{t("common.minRead", { n: a.reading_time })}</span></>)}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-gold group-hover:translate-x-0.5 mt-1.5" style={{ transitionProperty: "color, transform", transitionDuration: "160ms" }} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={BookOpen}
              title={t("dashboard.noArticles")}
              description={t("dashboard.noArticlesEmptyDesc")}
              actionLabel={user?.role === "admin" ? t("dashboard.createFirstArticle") : undefined}
              actionTo={user?.role === "admin" ? "/knowledge-base/new" : undefined}
            />
          )}
        </Card>

        {/* Announcements */}
        <Card className="lg:col-span-4 p-8">
          <CardHeader
            eyebrow={t("nav.announcements")}
            title={t("dashboard.latest")}
            action={
              <Link to="/announcements" className="text-xs text-zinc-400 hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>{t("common.all")}</Link>
            }
          />
          {data?.latest_announcements?.length ? (
            <div className="space-y-4">
              {data.latest_announcements.map((a) => (
                <Link to="/announcements" key={a.id} className="block group">
                  <div className="flex items-start gap-2">
                    {a.pinned && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{a.title}</div>
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
                        <span>{a.category}</span>
                        <span>·</span>
                        <span>{timeAgo(a.created_at)}</span>
                        {a.priority === "important" && <Badge variant="danger" size="xs" uppercase>{t("common.important")}</Badge>}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 text-center py-8">{t("dashboard.noAnnouncements")}</div>
          )}
        </Card>

        {/* Continue learning */}
        <Card className="lg:col-span-6 p-8">
          <CardHeader
            eyebrow={t("nav.academy")}
            title={t("dashboard.continueLearning")}
            action={
              <Link to="/academy" className="text-xs text-zinc-400 hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>{t("dashboard.allTrainings")}</Link>
            }
          />
          {data?.continue_learning?.length ? (
            <div className="space-y-5">
              {data.continue_learning.map((c) => (
                <div key={c.training.id}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-white truncate">{c.training.title}</div>
                    <div className="text-xs text-zinc-500 shrink-0 ml-3">{c.progress}%</div>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${c.progress}%`, transition: "width 400ms ease" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 text-center py-8 border border-dashed border-white/10 rounded-xl">
              {t("dashboard.noTrainingsInProgress")} <Link className="text-gold hover:underline" to="/academy">{t("dashboard.browseAcademy")}</Link>
            </div>
          )}
        </Card>

        {/* Recent downloads */}
        <Card className="lg:col-span-6 p-8">
          <CardHeader
            eyebrow={t("nav.downloads")}
            title={t("dashboard.recentlyAdded")}
            action={
              <Link to="/downloads" className="text-xs text-zinc-400 hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "180ms" }}>{t("common.all")}</Link>
            }
          />
          {data?.latest_downloads?.length ? (
            <div className="divide-y divide-white/5">
              {data.latest_downloads.map((d) => (
                <Link to="/downloads" key={d.id} className="flex items-center gap-3 py-3 group">
                  <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                    <FileText className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{d.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{d.category} · {timeAgo(d.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 text-center py-8 border border-dashed border-white/10 rounded-xl">{t("dashboard.noFiles")}</div>
          )}
        </Card>

        {/* Upcoming events */}
        <Card className="lg:col-span-12 p-8">
          <CardHeader
            eyebrow={t("dashboard.module")}
            title={t("dashboard.upcomingEvents")}
            action={<Calendar className="w-4 h-4 text-gold" strokeWidth={1.75} />}
          />
          {data?.upcoming_events?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.upcoming_events.map((e) => (
                <div key={e.id} className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="w-14 shrink-0 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-gold font-semibold">
                      {new Date(e.starts_at).toLocaleString(locale, { month: "short" })}
                    </div>
                    <div className="font-display text-2xl font-light text-white">
                      {new Date(e.starts_at).getDate()}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{e.title}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {new Date(e.starts_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
                      {e.location ? ` · ${e.location}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 text-center py-8">{t("dashboard.noEvents")}</div>
          )}
        </Card>
      </div>

      {/* AI teaser */}
      <Link to="/ai-assistant" className="block em-card em-card-hover p-8 group">
        <div className="flex items-center gap-6">
          <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">{t("dashboard.aiTeaserSubtitle")}</p>
            <h3 className="font-display text-xl mt-1">{t("dashboard.aiTeaserTitle")}</h3>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-gold group-hover:translate-x-1" style={{ transitionProperty: "color, transform", transitionDuration: "180ms" }} />
        </div>
      </Link>
    </div>
  );
}
